import type { Store } from "vuex";
import { toRaw } from "vue";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LOG = "[betslip-persistence]";
const VUEX_STORAGE_KEY = "vuex";
const OBSOLETE_BETSLIP_LS_KEYS = [
  "sis-betslip-meta",
  "sis-betslip-idb-migrated",
  "sis-betslip-snapshot",
] as const;
const WAGER_DEBOUNCE_MS = 400;
const PUT_RETRY_DELAYS_MS = [0, 200, 800];

const IDB_NAME = "sis-betslip";
const IDB_VERSION = 1;
const IDB_STORE = "bets";

const SKIP_VUEX_PERSIST_MUTATIONS = new Set([
  "saveBetslips",
  "setShowAddedNotify",
  "setActiveAmountItemId",
]);

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

type StoreAny = Store<any>;

type BetslipIdbRecord = {
  pk: string;
  accountKey: string;
  id: string;
  order: number;
  bet: Record<string, unknown>;
};

let storeRef: StoreAny | null = null;
let idbPromise: Promise<IDBDatabase | null> | null = null;
let idbPersistenceActive = false;
let betslipExcludedFromVuex = false;
let suppressBetslipPersist = false;
let legacyBetslipPersistTimer: ReturnType<typeof setTimeout> | null = null;

const lastCommittedByAccount = new Map<string, any[]>();
const lastPersistedSignatureByPk = new Map<string, string>();
const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
const generationByKey = new Map<string, number>();
const tombstones = new Set<string>();
const writeChains = new Map<string, Promise<void>>();
const restoreGenerationByAccount = new Map<string, number>();

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function betslipRecordKey(accountKey: string, betId: string): string {
  return `${accountKey}\u0000${betId}`;
}

function accountPkPrefix(accountKey: string): string {
  return `${accountKey}\u0000`;
}

function getBetslipsMap(store: StoreAny): Record<string, any[]> {
  return store.state.stateBetslip?.betslips ?? {};
}

function getAccountBets(accountKey: string): any[] {
  return storeRef?.state.stateBetslip?.betslips?.[accountKey] ?? [];
}

function bumpGeneration(pk: string): number {
  const generation = (generationByKey.get(pk) ?? 0) + 1;
  generationByKey.set(pk, generation);
  return generation;
}

function runWithoutPersist(fn: () => void) {
  suppressBetslipPersist = true;
  try {
    fn();
  } finally {
    suppressBetslipPersist = false;
  }
}

function removeObsoleteBetslipLocalStorageKeys() {
  for (const key of OBSOLETE_BETSLIP_LS_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore private mode / quota
    }
  }
}

// ---------------------------------------------------------------------------
// IndexedDB
// ---------------------------------------------------------------------------

function openBetslipDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);
    request.onerror = () => resolve(null);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        const store = db.createObjectStore(IDB_STORE, { keyPath: "pk" });
        store.createIndex("accountKey", "accountKey", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function getIdb(): Promise<IDBDatabase | null> {
  if (!idbPromise) {
    idbPromise = openBetslipDb();
  }
  return idbPromise;
}

function runIdbTransaction<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | void> {
  return getIdb().then((db) => {
    if (!db) {
      return Promise.reject(new Error("IndexedDB unavailable"));
    }

    return new Promise<T | void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, mode);
      const store = tx.objectStore(IDB_STORE);
      const request = fn(store);

      tx.oncomplete = () => {
        if (request && "result" in request) {
          resolve((request as IDBRequest<T>).result);
        } else {
          resolve(undefined);
        }
      };
      tx.onerror = () =>
        reject(tx.error ?? new Error("IndexedDB transaction failed"));
      tx.onabort = () =>
        reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    });
  });
}

async function putBetslipRecord(
  accountKey: string,
  betId: string,
  order: number,
  bet: Record<string, unknown>
): Promise<void> {
  const record: BetslipIdbRecord = {
    pk: betslipRecordKey(accountKey, betId),
    accountKey,
    id: betId,
    order,
    bet,
  };
  await runIdbTransaction("readwrite", (store) => store.put(record));
}

async function deleteBetslipRecord(
  accountKey: string,
  betId: string
): Promise<void> {
  await runIdbTransaction("readwrite", (store) =>
    store.delete(betslipRecordKey(accountKey, betId))
  );
}

async function clearBetslipAccount(accountKey: string): Promise<void> {
  const db = await getIdb();
  if (!db) {
    throw new Error("IndexedDB unavailable");
  }

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    const index = tx.objectStore(IDB_STORE).index("accountKey");
    index.openCursor(IDBKeyRange.only(accountKey)).onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB clear failed"));
  });
}

async function getBetslipRecordsByAccount(
  accountKey: string
): Promise<BetslipIdbRecord[]> {
  const db = await getIdb();
  if (!db) {
    return [];
  }

  return new Promise((resolve, reject) => {
    const request = db
      .transaction(IDB_STORE, "readonly")
      .objectStore(IDB_STORE)
      .index("accountKey")
      .getAll(IDBKeyRange.only(accountKey));

    request.onsuccess = () => {
      const rows = (request.result as BetslipIdbRecord[]) ?? [];
      rows.sort((a, b) => a.order - b.order);
      resolve(rows);
    };
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB read failed"));
  });
}

async function isBetslipIdbAvailable(): Promise<boolean> {
  return (await getIdb()) != null;
}

// ---------------------------------------------------------------------------
// Bet snapshots & signatures
// ---------------------------------------------------------------------------

function cloneBetSnapshot(bet: any): Record<string, unknown> {
  const raw = toRaw(bet);
  const structuredCloneFn = (
    globalThis as { structuredClone?: (value: unknown) => unknown }
  ).structuredClone;
  if (typeof structuredCloneFn === "function") {
    return structuredCloneFn(raw) as Record<string, unknown>;
  }
  return JSON.parse(JSON.stringify(raw));
}

function betPersistSignature(bet: any): string {
  return [
    bet.wager,
    bet.returnValue,
    bet.next_draws ?? "",
    bet.isSavedNumbers ? 1 : 0,
    bet.myNumberId ?? "",
    bet.nextDrawsShowInfo?.length ?? 0,
  ].join("|");
}

function rememberPersistedSignature(
  accountKey: string,
  betId: string,
  bet: any
) {
  lastPersistedSignatureByPk.set(
    betslipRecordKey(accountKey, betId),
    betPersistSignature(bet)
  );
}

function forgetPersistedSignature(accountKey: string, betId: string) {
  lastPersistedSignatureByPk.delete(betslipRecordKey(accountKey, betId));
}

// ---------------------------------------------------------------------------
// Write queue (debounce, generations, tombstones)
// ---------------------------------------------------------------------------

function enqueueWrite(pk: string, task: () => Promise<void>): Promise<void> {
  const next = (writeChains.get(pk) ?? Promise.resolve())
    .catch(() => undefined)
    .then(task)
    .catch((err) => console.warn(`${LOG} write failed:`, err));
  writeChains.set(pk, next);
  return next;
}

async function putWithRetry(
  accountKey: string,
  betId: string,
  order: number,
  bet: any,
  expectedGeneration: number
): Promise<void> {
  const pk = betslipRecordKey(accountKey, betId);

  for (let attempt = 0; attempt < PUT_RETRY_DELAYS_MS.length; attempt++) {
    const delay = PUT_RETRY_DELAYS_MS[attempt];
    if (delay > 0) {
      await new Promise((r) => setTimeout(r, delay));
    }
    if (generationByKey.get(pk) !== expectedGeneration || tombstones.has(pk)) {
      return;
    }

    try {
      await putBetslipRecord(accountKey, betId, order, cloneBetSnapshot(bet));
      return;
    } catch {
      if (attempt === PUT_RETRY_DELAYS_MS.length - 1) {
        console.warn(`${LOG} put abandoned after retries`, pk);
      }
    }
  }
}

function scheduleDebouncedPut(
  accountKey: string,
  betId: string,
  order: number,
  bet: any
) {
  const pk = betslipRecordKey(accountKey, betId);
  tombstones.delete(pk);

  const existingTimer = debounceTimers.get(pk);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  const generation = bumpGeneration(pk);
  debounceTimers.set(
    pk,
    setTimeout(() => {
      debounceTimers.delete(pk);
      void enqueueWrite(pk, () =>
        putWithRetry(accountKey, betId, order, bet, generation)
      );
    }, WAGER_DEBOUNCE_MS)
  );
}

function flushDebouncedPut(
  accountKey: string,
  betId: string,
  bet: any,
  order: number
) {
  const pk = betslipRecordKey(accountKey, betId);
  const timer = debounceTimers.get(pk);
  if (!timer) {
    return;
  }
  clearTimeout(timer);
  debounceTimers.delete(pk);

  const generation = generationByKey.get(pk) ?? 0;
  void enqueueWrite(pk, () =>
    putWithRetry(accountKey, betId, order, bet, generation)
  );
}

function cancelDebouncedPut(accountKey: string, betId: string) {
  const pk = betslipRecordKey(accountKey, betId);
  const timer = debounceTimers.get(pk);
  if (timer) {
    clearTimeout(timer);
    debounceTimers.delete(pk);
  }
}

function scheduleDelete(accountKey: string, betId: string) {
  const pk = betslipRecordKey(accountKey, betId);
  cancelDebouncedPut(accountKey, betId);
  tombstones.add(pk);
  const generation = bumpGeneration(pk);

  void enqueueWrite(pk, async () => {
    if (generationByKey.get(pk) !== generation) {
      return;
    }
    try {
      await deleteBetslipRecord(accountKey, betId);
    } catch (err) {
      console.warn(`${LOG} delete failed:`, err);
    }
  });
}

function invalidateAccountPendingWrites(accountKey: string) {
  const prefix = accountPkPrefix(accountKey);

  for (const pk of [...debounceTimers.keys()]) {
    if (!pk.startsWith(prefix)) {
      continue;
    }
    const timer = debounceTimers.get(pk);
    if (timer) {
      clearTimeout(timer);
    }
    debounceTimers.delete(pk);
  }

  for (const pk of [...generationByKey.keys()]) {
    if (pk.startsWith(prefix)) {
      bumpGeneration(pk);
      tombstones.add(pk);
    }
  }

  restoreGenerationByAccount.set(
    accountKey,
    (restoreGenerationByAccount.get(accountKey) ?? 0) + 1
  );
}

function scheduleClearAccount(accountKey: string) {
  invalidateAccountPendingWrites(accountKey);
  void enqueueWrite(`clear:${accountKey}`, async () => {
    try {
      await clearBetslipAccount(accountKey);
    } catch (err) {
      console.warn(`${LOG} clear account failed:`, err);
    }
  });
}

function processBetslipSave(accountKey: string, next: any[], prev: any[]) {
  if (!accountKey) {
    return;
  }
  if (!idbPersistenceActive) {
    scheduleLegacyBetslipLocalStoragePersist();
    return;
  }

  const prevIds = new Set(prev.map((b) => b.id));
  const nextIds = new Set(next.map((b) => b.id));

  if (next.length === 0 && prev.length > 0) {
    prev.forEach((bet) => forgetPersistedSignature(accountKey, bet.id));
    scheduleClearAccount(accountKey);
    return;
  }

  prev.forEach((bet) => {
    if (!nextIds.has(bet.id)) {
      forgetPersistedSignature(accountKey, bet.id);
      scheduleDelete(accountKey, bet.id);
    }
  });

  next.forEach((bet, order) => {
    const pk = betslipRecordKey(accountKey, bet.id);
    const nextSig = betPersistSignature(bet);
    const knownSig = lastPersistedSignatureByPk.get(pk);

    if (!prevIds.has(bet.id)) {
      tombstones.delete(pk);
      const generation = bumpGeneration(pk);
      rememberPersistedSignature(accountKey, bet.id, bet);
      void enqueueWrite(pk, () =>
        putWithRetry(accountKey, bet.id, order, bet, generation)
      );
      return;
    }

    if (knownSig !== nextSig) {
      scheduleDebouncedPut(accountKey, bet.id, order, bet);
      rememberPersistedSignature(accountKey, bet.id, bet);
    }
  });
}

// ---------------------------------------------------------------------------
// localStorage: legacy vuex slice (IndexedDB unavailable only)
// ---------------------------------------------------------------------------

function ensureAccountKeyFromQuery(store: StoreAny) {
  if (store.state.accountKey || typeof window === "undefined") {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const account = params.get("account");
  const currency = params.get("currency");
  if (account && currency) {
    store.commit("SET_ACCOUT", `${account}-${currency}`);
  }
}

function scheduleLegacyBetslipLocalStoragePersist() {
  if (idbPersistenceActive || !storeRef) {
    return;
  }
  if (legacyBetslipPersistTimer) {
    clearTimeout(legacyBetslipPersistTimer);
  }
  legacyBetslipPersistTimer = setTimeout(() => {
    legacyBetslipPersistTimer = null;
    try {
      const parsed = JSON.parse(localStorage.getItem(VUEX_STORAGE_KEY) ?? "{}");
      parsed.stateBetslip = storeRef?.state.stateBetslip;
      localStorage.setItem(VUEX_STORAGE_KEY, JSON.stringify(parsed));
    } catch (err) {
      console.warn(`${LOG} legacy betslip persist failed:`, err);
    }
  }, 0);
}

function stripBetslipFromVuexLocalStorage() {
  try {
    const raw = localStorage.getItem(VUEX_STORAGE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      delete parsed.stateBetslip;
      localStorage.setItem(VUEX_STORAGE_KEY, JSON.stringify(parsed));
    }
  } catch (err) {
    console.warn(`${LOG} failed to strip legacy betslip:`, err);
  }
}

// ---------------------------------------------------------------------------
// vuex-persistedstate hooks (exported)
// ---------------------------------------------------------------------------

export function markBetslipExcludedFromVuexPersist() {
  betslipExcludedFromVuex = true;
  stripBetslipFromVuexLocalStorage();
  removeObsoleteBetslipLocalStorageKeys();
}

export function getVuexPersistFilter(mutationType: string): boolean {
  return !SKIP_VUEX_PERSIST_MUTATIONS.has(mutationType);
}

export function getVuexPersistReducer(state: any): any {
  if (!state || typeof state !== "object") {
    return state;
  }
  const next = { ...state };
  // Cutoffs expire. Keeping this list across visits makes a later open look like
  // the game has no draws left, before the fresh next-draws request returns.
  delete next.nextDraws;
  if (betslipExcludedFromVuex) {
    delete next.stateBetslip;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Migration & restore
// ---------------------------------------------------------------------------

async function verifyMigration(betslips: Record<string, any[]>): Promise<boolean> {
  for (const [accountKey, bets] of Object.entries(betslips)) {
    const rows = await getBetslipRecordsByAccount(accountKey);
    if (rows.length !== bets.length) {
      return false;
    }
    for (let i = 0; i < bets.length; i++) {
      if (rows[i].id !== bets[i].id) {
        return false;
      }
    }
  }
  return true;
}

/** Copy betslips still rehydrated from legacy vuex localStorage into IndexedDB, then drop vuex slice. */
async function syncMemoryBetslipsToIdb(store: StoreAny) {
  const betslips = getBetslipsMap(store);
  const hasInMemory = Object.values(betslips).some(
    (bets) => (bets?.length ?? 0) > 0
  );

  if (hasInMemory) {
    for (const [accountKey, bets] of Object.entries(betslips)) {
      for (let order = 0; order < bets.length; order++) {
        const bet = bets[order];
        await putBetslipRecord(
          accountKey,
          bet.id,
          order,
          cloneBetSnapshot(bet)
        );
      }
    }

    if (!(await verifyMigration(betslips))) {
      console.warn(`${LOG} IndexedDB sync verification failed`);
      return;
    }
  }

  markBetslipExcludedFromVuexPersist();
}

async function restoreAccountFromIdb(accountKey: string) {
  if (!idbPersistenceActive || !storeRef || !accountKey) {
    return;
  }
  if (getAccountBets(accountKey).length > 0) {
    return;
  }

  const restoreGen = (restoreGenerationByAccount.get(accountKey) ?? 0) + 1;
  restoreGenerationByAccount.set(accountKey, restoreGen);

  let rows: BetslipIdbRecord[];
  try {
    rows = await getBetslipRecordsByAccount(accountKey);
  } catch (err) {
    console.warn(`${LOG} restore read failed:`, err);
    return;
  }

  if (
    restoreGenerationByAccount.get(accountKey) !== restoreGen ||
    getAccountBets(accountKey).length > 0
  ) {
    return;
  }

  const fromIdb = rows
    .filter((row) => !tombstones.has(row.pk))
    .map((row) => row.bet);

  runWithoutPersist(() => {
    if (!storeRef) {
      return;
    }
    lastCommittedByAccount.set(accountKey, fromIdb);
    storeRef.commit("saveBetslips", { key: accountKey, value: fromIdb });
  });
}

function seedLastCommitted(store: StoreAny) {
  for (const [key, bets] of Object.entries(getBetslipsMap(store))) {
    lastCommittedByAccount.set(key, bets);
    bets.forEach((bet) => rememberPersistedSignature(key, bet.id, bet));
  }
}

export function flushPendingBetslipWrites() {
  if (!storeRef || !idbPersistenceActive) {
    return Promise.resolve();
  }

  const accountKey = storeRef.state.accountKey as string;
  if (!accountKey) {
    return Promise.resolve();
  }

  getAccountBets(accountKey).forEach((bet, order) => {
    flushDebouncedPut(accountKey, bet.id, bet, order);
  });

  return Promise.all([...writeChains.values()]).then(() => undefined);
}

/** Wait until saveBetslips handlers and IndexedDB writes for the latest commit have settled. */
export async function awaitBetslipPersistenceAfterCommit(): Promise<void> {
  await Promise.resolve();
  await flushPendingBetslipWrites();
}

/** Keep vuex in sync for append/remove flows; skips IndexedDB (use persistBetslipDraftToStorage). */
export function syncBetslipDraftToStore(accountKey: string, bets: any[]) {
  if (!accountKey || !storeRef) {
    return;
  }
  lastCommittedByAccount.set(accountKey, bets);
  runWithoutPersist(() => {
    storeRef!.commit("saveBetslips", { key: accountKey, value: bets });
  });
}

/** IndexedDB / legacy slice only — call syncBetslipDraftToStore first when vuex must stay current. */
export function persistBetslipDraftToStorage(
  accountKey: string,
  bets: any[]
) {
  if (!accountKey || !storeRef) {
    return;
  }

  const prev = lastCommittedByAccount.get(accountKey) ?? [];
  lastCommittedByAccount.set(accountKey, bets);

  if (suppressBetslipPersist) {
    return;
  }

  if (!idbPersistenceActive) {
    storeRef.commit("saveBetslips", { key: accountKey, value: bets });
    return;
  }

  queueMicrotask(() => {
    processBetslipSave(accountKey, bets, prev);
  });
}

// ---------------------------------------------------------------------------
// Plugin
// ---------------------------------------------------------------------------

function flushOnPageExit() {
  void flushPendingBetslipWrites();
}

function attachLifecycleFlush() {
  if (typeof window === "undefined") {
    return;
  }
  window.addEventListener("pagehide", flushOnPageExit);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushOnPageExit();
    }
  });
}

async function bootstrapIdbPersistence(store: StoreAny) {
  if (!(await isBetslipIdbAvailable())) {
    console.warn(
      `${LOG} IndexedDB unavailable; using deferred localStorage for betslip only`
    );
    return;
  }

  idbPersistenceActive = true;
  await syncMemoryBetslipsToIdb(store);
  seedLastCommitted(store);

  const accountKey = store.state.accountKey as string;
  if (accountKey && getAccountBets(accountKey).length === 0) {
    await restoreAccountFromIdb(accountKey);
  }
}

function onSaveBetslips(mutation: { payload: { key: string; value: any[] } }) {
  const accountKey = mutation.payload.key;
  const prev = lastCommittedByAccount.get(accountKey) ?? [];
  const next = mutation.payload.value ?? [];
  lastCommittedByAccount.set(accountKey, next);

  if (suppressBetslipPersist) {
    return;
  }

  queueMicrotask(() => {
    processBetslipSave(accountKey, next, prev);
  });
}

export function createBetslipPersistencePlugin() {
  return (store: StoreAny) => {
    storeRef = store;
    ensureAccountKeyFromQuery(store);
    seedLastCommitted(store);
    attachLifecycleFlush();
    void bootstrapIdbPersistence(store);

    store.subscribe((mutation, state) => {
      if (mutation.type === "saveBetslips") {
        onSaveBetslips(mutation as { payload: { key: string; value: any[] } });
      }
      if (mutation.type === "SET_ACCOUT") {
        void restoreAccountFromIdb(state.accountKey as string);
      }
    });
  };
}

/**
 * Browser termination during the debounce window or before an IndexedDB transaction
 * completes may lose the latest wager edit. Adds, removes, and clears write immediately.
 */
